<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Highcharts } from '@/utils'
import type { GroupAggStats } from '@/store/stats/types'

type TDistribution = GroupAggStats['subjectInsights'][0]['distribution']

const props = withDefaults(
    defineProps<{
        subjectName?: string
        distribution: TDistribution
    }>(),
    { subjectName: '' }
)

const chartId = `group-distribution-chart__${Math.floor(Math.random() * 1e9)}`
const chart = ref<Highcharts.Chart | null>(null)

const numStudents = computed(
    () =>
        props.distribution.countBelow50
        + props.distribution.count50to75
        + props.distribution.countAbove75
)

const pctBelow50 = computed(() =>
    Math.round(
        ((props.distribution.countBelow50 || 0) / (numStudents.value || Infinity)) * 100
    )
)

const pct50to75 = computed(() =>
    Math.round(
        ((props.distribution.count50to75 || 0) / (numStudents.value || Infinity)) * 100
    )
)

const pctAbove75 = computed(() =>
    Math.round(
        ((props.distribution.countAbove75 || 0) / (numStudents.value || Infinity)) * 100
    )
)

onMounted(() => {
    const dist = props.distribution
    const total = numStudents.value
    chart.value = Highcharts.chart(chartId, {
        chart: {
            type: 'column',
            animation: false,
            backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        title: {
            text: undefined,
        },
        legend: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            backgroundColor: '#F4F5F9',
            outside: true,
            hideDelay: 0,
            useHTML: true,
            borderWidth: 0,
            formatter: function (tooltip) { // eslint-disable-line @typescript-eslint/no-unused-vars
                return `
                        <table>
                            <tr>
                                <td style="text-align:center;color:#475967">${this.key}</td>
                            </tr>
                            <tr>
                                <td style="text-align:center;font-weight:bold">${this.y} students</td>
                            </tr>
                        </table>`
            },
        },
        xAxis: {
            tickInterval: 10,
            min: 0,
            max: 100,
            labels: {
                step: 2,
                format: '{value}%',
            },
        },
        yAxis: {
            title: {
                text: '# of Students',
                style: {
                    color: '#475967',
                    fontSize: '10px',
                },
            },
            opposite: true,
            tickPixelInterval: 30,
            min: 0,
            minRange: 3,
        },
        plotOptions: {
            column: {
                color: '#1F3545',
                minPointLength: total > 0 ? 3 : 0,
                groupPadding: 0,
                pointPadding: 0.08,
                pointPlacement: 'on',
                borderWidth: 0,
                states: {
                    hover: {
                        color: '#FF6363',
                    },
                    inactive: {
                        opacity: 1,
                    },
                },
                stacking: 'normal',
            },
        },
        series: [{
            ['borderRadiusTopLeft' as 'borderRadius']: 3,
            ['borderRadiusTopRight' as 'borderWidth']: 3,
            data: [{
                x: 75,
                y: dist['75-80'],
                name: '75-80%',
                states: {
                    hover: {
                        color: '#14CA9E',
                    },
                },
            }],
        }, {
            ['borderRadiusTopLeft' as 'borderRadius']: (
                dist['70-75'] + dist['75-80'] === 0
            )
                ? 3
                : 0,
            ['borderRadiusTopRight' as 'borderWidth']: (
                dist['70-75'] + dist['75-80'] === 0
            )
                ? 3
                : 0,
            data: [{
                x: 75,
                y: dist['70-75'],
                name: (dist['70-75'] + dist['75-80'] === 0) ? '70-80%' : '70-75%',
                states: {
                    hover: {
                        color: '#FFBD47',
                    },
                },
            }],
        }, {
            ['borderRadiusTopLeft' as 'borderRadius']: 3,
            ['borderRadiusTopRight' as 'borderWidth']: 3,
            data: [{
                x: 5,
                y: dist['0-10'],
                name: '0-10%',
            }, {
                x: 15,
                y: dist['10-20'],
                name: '10-20%',
            }, {
                x: 25,
                y: dist['20-30'],
                name: '20-30%',
            }, {
                x: 35,
                y: dist['30-40'],
                name: '30-40%',
            }, {
                x: 45,
                y: dist['40-50'],
                name: '40-50%',
            }, {
                x: 55,
                y: dist['50-60'],
                name: '50-60%',
                states: {
                    hover: {
                        color: '#FFBD47',
                    },
                },
            }, {
                x: 65,
                y: dist['60-70'],
                name: '60-70%',
                states: {
                    hover: {
                        color: '#FFBD47',
                    },
                },
            }, {
                x: 85,
                y: dist['80-90'],
                name: '80-90%',
                states: {
                    hover: {
                        color: '#14CA9E',
                    },
                },
            }, {
                x: 95,
                y: dist['90-100'],
                name: '90-100%',
                states: {
                    hover: {
                        color: '#14CA9E',
                    },
                },
            }],
        }],
    } as Highcharts.Options)
})
</script>

<template>
    <div class="group-distribution-chart">
        <div class="group-distribution-chart__subject-name">
            {{ props.subjectName }}
        </div>
        <div class="group-distribution-chart__data">
            <div
                :id="chartId"
                class="group-distribution-chart__chart"
            />
            <div class="group-distribution-chart__legend">
                <div class="group-distribution-chart__legend-labels">
                    <div class="group-distribution-chart__legend-label group-distribution-chart__legend-label--green">
                        Subject Score above 75%
                    </div>
                    <div class="group-distribution-chart__legend-label group-distribution-chart__legend-label--yellow">
                        Subject Score of 50-75%
                    </div>
                    <div class="group-distribution-chart__legend-label group-distribution-chart__legend-label--red">
                        Subject Score below 50%
                    </div>
                </div>
                <div class="group-distribution-chart__legend-values">
                    <div class="group-distribution-chart__legend-value">
                        <span class="group-distribution-chart__legend-value-number">{{ pctAbove75 }}</span>
                        <span class="group-distribution-chart__legend-value-pct">%</span>
                    </div>
                    <div class="group-distribution-chart__legend-value">
                        <span class="group-distribution-chart__legend-value-number">{{ pct50to75 }}</span>
                        <span class="group-distribution-chart__legend-value-pct">%</span>
                    </div>
                    <div class="group-distribution-chart__legend-value">
                        <span class="group-distribution-chart__legend-value-number">{{ pctBelow50 }}</span>
                        <span class="group-distribution-chart__legend-value-pct">%</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">


.group-distribution-chart {
    &__subject-name {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
    }

    &__data {
        display: flex;
        margin-left: -10px;
    }

    &__loader,
    &__chart {
        width: 62%;
        height: 163px;

        &--placeholder {
            background-image: url('@/assets/images/group-distribution-placeholder.svg');
            background-repeat: no-repeat;
            background-position: 14px bottom;
            background-size: 76% 100%;
        }
    }

    &__legend {
        display: flex;
        flex-wrap: nowrap;
        padding-top: 12px;
    }

    &__legend-labels {
        margin-left: 32px;
        margin-right: 50px;
        padding-left: 18px;
        color: $slate-01;
        font-size: 12px;
        letter-spacing: 0.22px;
        line-height: 16px;
    }

    &__legend-label {
        position: relative;
        margin: 15px 0;
        white-space: nowrap;

        &::before {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            left: -14px;
            border-radius: 100%;
            width: 6px;
            height: 6px;
        }

        &--green {
            &::before {
                background-color: #0ab98f;
            }
        }

        &--yellow {
            &::before {
                background-color: #ffbd47;
            }
        }

        &--red {
            &::before {
                background-color: #ff6363;
            }
        }
    }

    &__legend-values {
        font-size: 18px;
    }

    &__legend-value {
        margin-bottom: 16px;
        line-height: 15px;
        text-align: right;

        &:first-child {
            margin-top: 14px;
        }
    }

    &__legend-value-number {
        font-weight: 600;
        letter-spacing: -0.3px;
    }

    &__legend-value-pct {
        letter-spacing: 0.48px;
    }
}
</style>
